import { useEffect, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { useAuthStore } from '../store/authStore'
import { activateSignup, validateActivationToken } from '../services/api'
import { errorText } from '../utils/errorText'

const FIELD_CLASS =
  'mt-1 block w-full rounded-md bg-theme-input border border-theme-border-primary text-theme-text-primary px-3 py-2 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 focus:outline-none'

const INVALID_LINK_TEXT = 'This activation link is invalid or has expired.'

/**
 * The activation link's landing page: the sign-up's email is shown, the password is set here,
 * and the account is created and signed in on submit.
 */
export default function Activate() {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const token = searchParams.get('token')
  const { login: setAuth } = useAuthStore()

  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [error, setError] = useState<string | null>(null)
  // The backend answers 409 when the address got an account by another route since the
  // link was sent; the page then offers the way in.
  const [alreadyRegistered, setAlreadyRegistered] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [isValidating, setIsValidating] = useState(true)
  const [tokenValid, setTokenValid] = useState(false)
  const [email, setEmail] = useState<string | null>(null)

  useEffect(() => {
    let cancelled = false
    const check = async () => {
      if (!token) {
        setIsValidating(false)
        return
      }
      try {
        const response = await validateActivationToken(token)
        if (cancelled) return
        if (response.valid) {
          setTokenValid(true)
          setEmail(response.email ?? null)
        }
      } catch {
        // An unanswered check reads as an unusable link; the visitor can request another.
      } finally {
        if (!cancelled) setIsValidating(false)
      }
    }
    check()
    return () => {
      cancelled = true
    }
  }, [token])

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    if (password.length < 8) {
      setError('Password must be at least 8 characters long')
      return
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    setIsLoading(true)
    try {
      const response = await activateSignup(token!, password)
      setAuth(response)
      navigate('/')
    } catch (err: any) {
      setAlreadyRegistered(err?.response?.status === 409)
      setError(errorText(err, 'Activation failed. Please try again.'))
    } finally {
      setIsLoading(false)
    }
  }

  const header = (
    <div className="flex items-center justify-center space-x-3 mb-6">
      <img src="/aac.png" alt="Agentic Packr" className="w-10 h-10 object-contain" />
      <h2 className="text-2xl font-bold text-theme-text-primary">Agentic Packr</h2>
    </div>
  )

  if (isValidating) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-secondary">
        <div className="max-w-md w-full bg-theme-card rounded-lg shadow-lg p-8 border border-theme-border-primary">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
            <span className="ml-3 text-theme-text-secondary">Checking your link…</span>
          </div>
        </div>
      </div>
    )
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-theme-secondary">
        <div className="max-w-md w-full bg-theme-card rounded-lg shadow-lg p-8 border border-theme-border-primary text-center">
          {header}
          <div className="mb-6 p-4 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md" role="alert">
            <p className="text-sm text-red-700 dark:text-red-400">{INVALID_LINK_TEXT}</p>
          </div>
          <Link to="/signup" className="inline-block w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors">
            Request a new link
          </Link>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-theme-secondary">
      <div className="max-w-md w-full bg-theme-card rounded-lg shadow-lg p-8 border border-theme-border-primary">
        {header}

        <div className="mb-6">
          <h3 className="text-lg font-semibold text-theme-text-primary mb-2">Set your password</h3>
          {email && (
            <p className="text-sm text-theme-text-secondary">
              Activating the account for <span className="font-medium text-theme-text-primary" data-testid="activation-email">{email}</span>
            </p>
          )}
        </div>

        {error && (
          <div className="mb-4 p-3 bg-red-100 dark:bg-red-900/30 border border-red-300 dark:border-red-700 rounded-md" role="alert">
            <p className="text-sm text-red-700 dark:text-red-400">{error}</p>
            {alreadyRegistered && (
              <Link to="/login" className="mt-2 inline-block text-sm text-blue-500 hover:text-blue-400">
                Sign in
              </Link>
            )}
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-theme-text-secondary mb-1">Password</label>
            <input
              type="password"
              required
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={FIELD_CLASS}
              placeholder="At least 8 characters"
              minLength={8}
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-theme-text-secondary mb-1">Confirm password</label>
            <input
              type="password"
              required
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className={FIELD_CLASS}
              placeholder="Confirm your password"
            />
          </div>
          <button
            type="submit"
            disabled={isLoading}
            className="w-full py-2 px-4 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {isLoading ? 'Creating your account...' : 'Create account'}
          </button>
        </form>
      </div>
    </div>
  )
}
